import { DELETE, GET, PATCH, POST } from '@/shared/api/requests';
import type { BugReportPayload, BugReport, SupportVideoGuide, SupportVideoGuidePayload } from '../types/types';

const BASE_URL = '/support';

const uploadFileToGCS = (
  file: File,
  signedUrl: string,
  contentType: string,
  onProgress?: (percent: number) => void,
): Promise<void> =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    if (onProgress) {
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
      });
    }
    xhr.addEventListener('load', () => {
      xhr.status === 200 ? resolve() : reject(new Error(`Upload failed: ${xhr.status}`));
    });
    xhr.addEventListener('error', () => reject(new Error('Upload failed')));
    xhr.open('PUT', signedUrl);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.send(file);
  });

export const supportApi = {
  submitBugReport: async (payload: BugReportPayload): Promise<BugReport> => {
    // Step 1: Create the report (text fields only)
    const createResponse = await POST<{ subject: string; description: string }>(
      `${BASE_URL}/bug-report/`,
      { subject: payload.subject, description: payload.description }
    );
    const report: BugReport = createResponse.data;

    // Step 2: If images provided, get signed URLs, upload directly to GCS, then confirm
    if (payload.images && payload.images.length > 0) {
      const files = payload.images.map((f) => ({
        name: f.name,
        content_type: f.type || 'image/png',
      }));

      const urlResponse = await POST<{ files: typeof files }>(
        `${BASE_URL}/bug-reports/${report.uuid}/image-upload-urls/`,
        { files }
      );
      const urls: Record<string, { signed_url: string; content_type: string; gcs_url: string }> = urlResponse.data.urls;

      // Upload each file directly to GCS via signed URL, using the exact content_type that was signed
      await Promise.all(
        payload.images.map((file) =>
          uploadFileToGCS(file, urls[file.name].signed_url, urls[file.name].content_type)
        )
      );

      // Step 3: Confirm the uploaded image URLs with the backend
      const gcsUrls = payload.images.map((file) => urls[file.name].gcs_url);
      const confirmResponse = await POST<{ image_urls: string[] }>(
        `${BASE_URL}/bug-reports/${report.uuid}/confirm-images/`,
        { image_urls: gcsUrls }
      );
      return confirmResponse.data;
    }

    return report;
  },

  getMyReports: async (): Promise<BugReport[]> => {
    const response = await GET(`${BASE_URL}/bug-reports/`);
    return response.data;
  },

  getImageSignedUrls: async (reportUuid: string): Promise<string[]> => {
    const response = await GET(`${BASE_URL}/bug-reports/${reportUuid}/image-signed-urls/`);
    return response.data.signed_urls;
  },

  getVideoGuides: async (): Promise<SupportVideoGuide[]> => {
    const response = await GET<SupportVideoGuide[]>(`${BASE_URL}/video-guides/`);
    return response.data;
  },

  getVideoGuidePlaybackUrl: async (guideId: string): Promise<string> => {
    const response = await GET<{ playback_url: string }>(
      `${BASE_URL}/video-guides/${guideId}/playback-url/`,
    );
    return response.data.playback_url;
  },

  createVideoGuide: async (
    payload: SupportVideoGuidePayload,
    onProgress?: (percent: number) => void,
  ): Promise<SupportVideoGuide> => {
    const uploadUrlResponse = await POST<{ file_name: string; content_type: string }>(
      `${BASE_URL}/video-guides/upload-url/`,
      {
        file_name: payload.video.name,
        content_type: payload.video.type || 'video/mp4',
      },
    );
    const { signed_url, gcs_path } = uploadUrlResponse.data;

    await uploadFileToGCS(payload.video, signed_url, payload.video.type || 'video/mp4', onProgress);

    const createResponse = await POST<{
      title: string;
      feature: string;
      description: string;
      video_gcs_path: string;
      sort_order: number;
      is_active: boolean;
    }>(`${BASE_URL}/video-guides/`, {
      title: payload.title,
      feature: payload.feature,
      description: payload.description,
      video_gcs_path: gcs_path,
      sort_order: payload.sort_order ?? 0,
      is_active: payload.is_active ?? true,
    });
    return createResponse.data;
  },

  updateVideoGuide: async (
    guideId: string,
    payload: Partial<Omit<SupportVideoGuidePayload, 'video'>> & { video?: File | null },
    onProgress?: (percent: number) => void,
  ): Promise<SupportVideoGuide> => {
    let video_gcs_path: string | undefined;
    if (payload.video) {
      const uploadUrlResponse = await POST<{ file_name: string; content_type: string }>(
        `${BASE_URL}/video-guides/upload-url/`,
        {
          file_name: payload.video.name,
          content_type: payload.video.type || 'video/mp4',
        },
      );
      video_gcs_path = uploadUrlResponse.data.gcs_path;
      await uploadFileToGCS(
        payload.video,
        uploadUrlResponse.data.signed_url,
        payload.video.type || 'video/mp4',
        onProgress,
      );
    }

    const response = await PATCH(`${BASE_URL}/video-guides/${guideId}/`, {
      ...payload,
      video: undefined,
      ...(video_gcs_path ? { video_gcs_path } : {}),
    });
    return response.data;
  },

  deleteVideoGuide: async (guideId: string): Promise<void> => {
    await DELETE(`${BASE_URL}/video-guides/${guideId}/`);
  },
};
